// client/src/components/stats/MatchupBreakdown.tsx
//
// Muestra el breakdown de un stat por matchup posicional (hero vs villain).
// Usado para CBet Flop, 3Bet by matchup y Fold-to-3Bet by matchup.
//
// El componente recibe una función `getBenchmark` opcional. Si se provee,
// muestra una columna extra con el rango saludable y un dot de evaluación
// (ok/warn/critical). Si no se provee, solo muestra los datos crudos.
//
// La evaluación contra benchmark solo se hace si el sample es ≥ MIN_SAMPLE_FOR_BENCHMARK_EVAL,
// para evitar que el ruido estadístico de samples chicos genere falsos leaks.

import React from 'react';
import { MatchupDerived, MatchupKey } from '../../utils/pokerStats/types';
import { MIN_MATCHUP_SAMPLE } from '../../utils/pokerStats/constants';
import { useT } from '../../lib/i18n';
import { Benchmark, evalBenchmark, MIN_SAMPLE_FOR_BENCHMARK_EVAL } from './benchmarks';

interface MatchupBreakdownProps {
  matchups: MatchupDerived[];
  /** Optional title shown above the table. If omitted, no title is rendered. */
  label?: string;
  /**
   * Optional benchmark provider. Si se pasa, la tabla muestra una columna
   * de "Healthy" y evalúa cada matchup contra su benchmark.
   */
  getBenchmark?: (matchup: MatchupKey) => Benchmark | null;
  /**
   * Override de la columna del valor principal (ej: "3Bet %" en lugar de "CBet %").
   * Por default usa la key 'stats.matchup.thCbet'.
   */
  valueColumnLabel?: string;
  /**
   * Si false, oculta la columna "Success %". Util para 3bet/fold3bet
   * donde el concepto de "success" no aplica.
   */
  showSuccessColumn?: boolean;
}

export function MatchupBreakdown({
  matchups,
  label,
  getBenchmark,
  valueColumnLabel,
  showSuccessColumn = true,
}: MatchupBreakdownProps) {
  const { t } = useT();

  const matchupLabel = (m: MatchupDerived): string => {
    if (m.key === 'multi') return t('stats.matchup.multi');
    if (m.heroPos && m.villainPos) {
      return `${m.heroPos} vs ${m.villainPos}`;
    }
    return String(m.key);
  };

  if (matchups.length === 0) {
    return (
      <div className="matchup-breakdown matchup-empty">
        <p className="matchup-empty-text">{t('stats.matchup.empty')}</p>
      </div>
    );
  }

  const sufficient = matchups.filter(m => !m.lowSample);
  const insufficient = matchups.filter(m => m.lowSample);
  const showBenchmarkCol = !!getBenchmark;

  // Helper para renderizar la fila de un matchup con benchmark eval
  const renderRow = (m: MatchupDerived, dim: boolean) => {
    const benchmark = getBenchmark ? getBenchmark(m.key) : null;
    // Solo evaluamos si hay benchmark Y el sample es suficiente para confiar
    const canEvaluate = benchmark && m.opp >= MIN_SAMPLE_FOR_BENCHMARK_EVAL;
    const status = canEvaluate ? evalBenchmark(m.cbetPct, benchmark) : 'no-benchmark';

    const rowClass = ['matchup-row', dim ? 'matchup-row-low' : '', `matchup-row-${status}`]
      .filter(Boolean)
      .join(' ');

    return (
      <tr key={m.key} className={rowClass}>
        <td className="matchup-cell-label">{matchupLabel(m)}</td>
        <td className="matchup-cell-num matchup-cell-value">{m.cbetPct}%</td>
        {showBenchmarkCol && (
          <td className="matchup-cell-num matchup-cell-benchmark">
            {benchmark ? `${benchmark.min}–${benchmark.max}%` : '—'}
          </td>
        )}
        <td className="matchup-cell-num matchup-cell-sample">n={m.opp}</td>
        {showSuccessColumn && (
          <td className="matchup-cell-num matchup-cell-success">
            {m.count > 0 ? `${m.successPct}%` : '—'}
          </td>
        )}
        {showBenchmarkCol && (
          <td className="matchup-cell-status">
            {canEvaluate && status !== 'no-benchmark' && (
              <span className={`stat-dot dot-${status}`} />
            )}
          </td>
        )}
      </tr>
    );
  };

  return (
    <div className="matchup-breakdown">
      {label && <div className="matchup-breakdown-title">{label}</div>}

      {sufficient.length === 0 && (
        <div className="matchup-warning">
          {t('stats.matchup.warning', { n: MIN_MATCHUP_SAMPLE })}
        </div>
      )}

      {sufficient.length > 0 && (
        <table className="matchup-table">
          <thead>
            <tr>
              <th>{t('stats.matchup.thMatchup')}</th>
              <th className="matchup-th-num">{valueColumnLabel || t('stats.matchup.thCbet')}</th>
              {showBenchmarkCol && (
                <th className="matchup-th-num">{t('stats.matchup.thBenchmark')}</th>
              )}
              <th className="matchup-th-num">{t('stats.matchup.thSample')}</th>
              {showSuccessColumn && (
                <th className="matchup-th-num">{t('stats.matchup.thSuccess')}</th>
              )}
              {showBenchmarkCol && <th className="matchup-th-status" />}
            </tr>
          </thead>
          <tbody>{sufficient.map(m => renderRow(m, false))}</tbody>
        </table>
      )}

      {insufficient.length > 0 && (
        <details className="matchup-insufficient">
          <summary>
            {t('stats.matchup.moreInsufficient', { n: insufficient.length })}
          </summary>
          <table className="matchup-table matchup-table-dim">
            <tbody>{insufficient.map(m => renderRow(m, true))}</tbody>
          </table>
        </details>
      )}

      {showBenchmarkCol && sufficient.length > 0 && (
        <p className="matchup-benchmark-disclaimer">
          {t('stats.matchup.benchmarkDisclaimer')}
        </p>
      )}
    </div>
  );
}
